import { motion } from "framer-motion";
import { Section } from "../components/Section";
import { SEO } from "../components/SEO";

export function SummerCampPage() {
  return (
    <>
      <SEO
        title="Summer Camp 2026 - VEX Robotics"
        description="Join our intensive VEX Robotics summer camp in Forsyth County, GA. Learn engineering, coding, and teamwork through hands-on robotics projects. Perfect for students ages 8-18."
        canonical="/summer-camp"
        keywords={[
          "VEX Robotics summer camp",
          "robotics summer camp",
          "STEM summer camp",
          "robotics camp Georgia",
          "VEX IQ summer camp",
          "VEX V5 summer camp",
          "robotics engineering camp",
          "coding summer camp",
        ]}
      />
      <div className="bg-neutral-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 py-24 text-gray-100">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <motion.h1
                  className="text-4xl md:text-5xl font-bold tracking-tight text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  VEX IQ Robotics Summer Camp 2026
                </motion.h1>
                <motion.p
                  className="text-xl text-brand-400 font-semibold"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  Intensive Learning Experience for Ages 8-14
                </motion.p>
                <motion.p
                  className="text-lg text-gray-300 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Join us next summer for an exciting week-long robotics
                  adventure where students work in teams to design, build, and
                  program robots for a special summer challenge. Our intensive
                  camp combines hands-on learning with friendly competition and
                  teamwork.
                </motion.p>
                <motion.p
                  className="text-base text-gray-400"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.25 }}
                >
                  We'll share detailed information, dates, and registration
                  details in early 2026.
                </motion.p>
                <motion.div
                  className="flex flex-col sm:flex-row items-center gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <motion.a
                    href="/contact"
                    className="w-full sm:w-auto rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Contact Us
                  </motion.a>
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                <img
                  src="/media/summer-camp-program.jpeg"
                  alt="Students working together at VEX IQ Robotics Summer Camp"
                  className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Camp Overview Section */}
        <Section>
          <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                Camp Overview
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                A comprehensive week-long robotics experience coming next summer
                2026, designed to accelerate learning and build lasting skills
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-brand-400">5</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-100 mb-2">
                  Days
                </h3>
                <p className="text-gray-300">
                  Intensive learning with 4 days of building and 1 day of
                  competition
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-brand-400">3-5</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-100 mb-2">
                  Team Size
                </h3>
                <p className="text-gray-300">
                  Collaborative learning in small teams for personalized
                  attention
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-brand-400">
                    8-14
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-100 mb-2">
                  Age Range
                </h3>
                <p className="text-gray-300">
                  Perfect for elementary and middle school students
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Daily Schedule Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  Daily Schedule
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Structured Learning with Hands-On Experience
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-bold text-brand-400">
                      1-4
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-100 mb-2">
                      Building & Programming Days
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Students work in teams to design, build, and program their
                      robots for the summer challenge. Each day focuses on
                      different aspects: structure, sensors, programming, and
                      testing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-bold text-brand-400">5</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-100 mb-2">
                      Competition Day
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Teams compete for awards in design, programming, and
                      teamwork. Students present their robots, demonstrate their
                      skills, and celebrate their achievements with family and
                      friends.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                What Students Learn
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Robot design principles and engineering concepts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Block-based programming and sensor integration</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Teamwork, communication, and problem-solving</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Competition strategy and robot optimization</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Documentation and presentation skills</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Registration & Details Section */}
        <Section>
          <div className="bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                Ready to Learn More?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Get early information about our VEX IQ Robotics Summer Camp 2026
                and learn more about what we have planned for next summer
              </p>
              <p className="text-base text-gray-400 mt-4">
                We'll share detailed information, dates, and registration
                details in early 2026.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <h3 className="text-xl font-semibold text-gray-100 mb-4">
                  Camp Details
                </h3>
                <div className="space-y-3 text-gray-300">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="text-brand-400">5 days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hours:</span>
                    <span className="text-brand-400">9:00 AM - 3:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Location:</span>
                    <span className="text-brand-400">Forsyth County</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Equipment:</span>
                    <span className="text-brand-400">Provided</span>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <h3 className="text-xl font-semibold text-gray-100 mb-4">
                  What's Included
                </h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center space-x-2">
                    <span className="text-brand-400">✓</span>
                    <span>All VEX IQ robotics equipment</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-brand-400">✓</span>
                    <span>Daily instruction and guidance</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-brand-400">✓</span>
                    <span>Competition entry and awards</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-brand-400">✓</span>
                    <span>Take-home project portfolio</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-400 mt-4">
                Questions about next summer's camp?{" "}
                <a href="/contact" className="text-brand-400 hover:underline">
                  Contact us
                </a>
              </p>
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}
